package fr.manyverse.app.presentation.dice3d

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import fr.manyverse.app.infrastructure.haptic.rememberHapticVibrator
import kotlin.time.TimeSource

/**
 * Intervalle minimal entre deux impulsions. En deçà, un moteur haptique ne rend plus deux chocs
 * distincts mais un ronronnement continu — et sur iOS les impulsions surnuméraires sont
 * simplement avalées. Une poignée de dés en produit bien davantage : ce plancher est ce qui
 * transforme une grêle de contacts en un rythme qui se lit sous les doigts.
 */
private const val MIN_HAPTIC_INTERVAL_MS = 45L

/**
 * Bornes de chaque signature, interpolées par la force du choc.
 *
 * La durée porte la **texture**, l'amplitude porte la **violence**. C'est le contraste entre les
 * deux qui rend les trois contacts reconnaissables : long et sourd pour le feutre qui absorbe,
 * bref et fort pour le bord qui claque, très bref et moyen pour l'entrechoc de deux dés.
 */
private data class HapticSignature(
    val minDurationMs: Long,
    val maxDurationMs: Long,
    val minAmplitude: Int,
    val maxAmplitude: Int
)

private val FELT_SIGNATURE = HapticSignature(16L, 30L, 55, 150)
private val WALL_SIGNATURE = HapticSignature(8L, 14L, 100, 255)
private val DIE_SIGNATURE = HapticSignature(6L, 11L, 80, 200)

private fun signatureFor(kind: DiceImpactKind): HapticSignature = when (kind) {
    DiceImpactKind.FELT -> FELT_SIGNATURE
    DiceImpactKind.WALL -> WALL_SIGNATURE
    DiceImpactKind.DIE -> DIE_SIGNATURE
}

/**
 * Traduit les chocs de [DiceRollSimulation] en vibrations : chaque contact réel des dés se sent
 * dans la main, avec sa force et sa texture.
 *
 * Sur les plateformes sans moteur haptique (bureau, web), le vibreur sous-jacent est déjà neutre :
 * rien à garder ici, l'appelant n'a pas à se demander sur quoi il tourne.
 */
@Composable
fun rememberDiceImpactHaptics(): (DiceImpact) -> Unit {
    val vibrate = rememberHapticVibrator()
    return remember(vibrate) {
        // TimeSource.Monotonic, et non l'horloge du système : c'est du stdlib Kotlin, donc
        // disponible sur toutes les cibles, et il ne recule pas si l'utilisateur change l'heure.
        var lastPulse: TimeSource.Monotonic.ValueTimeMark? = null
        ({ impact: DiceImpact ->
            val previous = lastPulse
            // Le tout premier choc d'un lancer part sans attendre : c'est celui de l'atterrissage.
            if (previous == null ||
                previous.elapsedNow().inWholeMilliseconds >= MIN_HAPTIC_INTERVAL_MS
            ) {
                lastPulse = TimeSource.Monotonic.markNow()
                val signature = signatureFor(impact.kind)
                val strength = impact.strength.coerceIn(0f, 1f)
                val duration = lerp(signature.minDurationMs, signature.maxDurationMs, strength)
                val amplitude = lerp(signature.minAmplitude, signature.maxAmplitude, strength)
                vibrate(amplitude, duration)
            }
        })
    }
}

private fun lerp(from: Long, to: Long, fraction: Float): Long =
    from + ((to - from) * fraction).toLong()

private fun lerp(from: Int, to: Int, fraction: Float): Int =
    from + ((to - from) * fraction).toInt()
